import { GridColDef } from "@mui/x-data-grid-pro";
import {
  CheckIcon,
  DocumentIcon,
  EyeIcon,
  GKButton,
  GKDataGrid,
  GKModal,
  Grid2,
  IconButton,
  ResetIcon,
  Tooltip,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, htmlToText, refetchQuery } from "utils/helpers";
import { moneyCommaSeparator } from "utils/MathFunction";
import useDocumentTitle from "utils/useDocumentTitle";
import OrderPortfolioTradesModal from "./OrderPortfolioTradesModal";

interface TradesOfPortfolioInterface {
  isConverted: boolean;
  id: string;
  nse: string;
  bse: string;
  company: string;
  quantity: number;
  price: number;
  transactionType: string;
  smallcaseStatus: string;
  smallcaseError: any;
  filledQuantity: number;
  orderDate: string;
}

const OrderManagerPortfolios = () => {
  useDocumentTitle("Order Manager Portfolios");

  const [allTradesOfPortfolio, setAllTradesOfPortfolio] = useState<
    TradesOfPortfolioInterface[]
  >([]);
  const [openRationalModal, setOpenRationalModal] = useState(false);

  const { state } = useLocation();

  const [selectedData, setSelectedData] = useState(null);

  const { orderId } = useParams();

  const [showTradesModal, setShowTradesModal] = useState(false);

  const requestBody = useGenerateQueryParams(
    "equityBasketOrderManagerPortfolio",
  );

  const { data, isFetching } = useFetchQuery({
    key: ["GET_ALL_PORTFOLIO_BY_ORDER_ID", requestBody],
    route: `/model_portfolio/${orderId}/order-manager-portfolios/`,
    requestBody,
  });

  const { mutate: getPortfolioTrade } = useMutation({
    mutationFn: (portfolioId: any) =>
      axiosInstance.get(
        `/model_portfolio/${orderId}/order-manager-trade/${portfolioId}/`,
      ),
    onSuccess: (response) => {
      setShowTradesModal(true);
      setAllTradesOfPortfolio(response?.data?.data);
    },
  });

  const { mutate: handleConvertTrade, isLoading } = useMutation({
    mutationFn: (data: { portfolioId: number[]; isRevert: boolean }) =>
      axiosInstance.post(
        `/model_portfolio/${orderId}/order-manager-trade-convert/`,
        data,
      ),
    onSuccess: () => refetchQuery("GET_ALL_PORTFOLIO_BY_ORDER_ID"),
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Portfolio Name", "portfolio", 200, 1),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography fontWeight={700}>{params?.row?.clientName}</Typography>
            <Typography fontSize={12}>
              {params?.row?.name} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Net Trade AMT. (₹)", "netTradeAmount", 150, 1),
      type: "number",
      valueFormatter: (params) => moneyCommaSeparator(params?.value),
    },
    {
      ...extraColumnsField("Buy AMT. (₹)", "totalBuyAmount", 150, 1),
      type: "number",
      valueFormatter: (params) => moneyCommaSeparator(params?.value),
    },
    {
      ...extraColumnsField("Sell AMT. (₹)", "totalSellAmount", 150, 1),
      type: "number",
      valueFormatter: (params) => moneyCommaSeparator(params?.value),
    },
    { ...extraColumnsField("Sending Mode", "sendingMode", 150, 1) },
    { ...extraColumnsField("Broker", "broker", 150, 1) },
    {
      align: "right",
      headerAlign: "right",
      ...extraColumnsField("Actions", "action", 150, 1),
      renderCell: (params) => {
        return (
          <Grid2 container gap={1}>
            <IconButton
              onClick={() => {
                setSelectedData(params.row);
                getPortfolioTrade(params?.row?.id);
              }}
            >
              <EyeIcon height={16} width={16} />
            </IconButton>
            {params?.row?.isConverted ? (
              <Tooltip title="Revert from tradebook" placement="left">
                <IconButton
                  color="error"
                  disabled={isLoading}
                  sx={{
                    borderRadius: "25.6px",
                    background: "#F9EEED",
                    "&:hover": {
                      background: "#E1A9A2",
                    },
                  }}
                  onClick={() =>
                    handleConvertTrade({
                      portfolioId: [params?.row?.id],
                      isRevert: true,
                    })
                  }
                >
                  <ResetIcon height={16} width={16} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Add to tradebook" placement="left">
                <IconButton
                  color="success"
                  disabled={isLoading}
                  onClick={() =>
                    handleConvertTrade({
                      portfolioId: [params?.row?.id],
                      isRevert: false,
                    })
                  }
                  sx={{
                    borderRadius: "25.6px",
                    background: "#E8F3EC",
                    "&:hover": {
                      background: "#8AC09E",
                    },
                  }}
                >
                  <CheckIcon height={16} width={16} />
                </IconButton>
              </Tooltip>
            )}
          </Grid2>
        );
      },
    },
  ];

  return (
    <DashboardLayout title="Order Manager Portfolios">
      <Grid2 container>
        <Grid2 size={12}>
          <GKDataGrid
            rows={data?.data?.data || []}
            loading={isFetching}
            name="equityBasketOrderManagerPortfolio"
            totalCount={data?.data?.totalCount || 0}
            columns={columns}
            headerComponent={
              <GKButton
                variant="outlined"
                color="dark"
                startIcon={<DocumentIcon />}
                onClick={() => setOpenRationalModal(true)}
              >
                View Rationale
              </GKButton>
            }
          />
        </Grid2>
      </Grid2>
      {showTradesModal && (
        <OrderPortfolioTradesModal
          open={showTradesModal}
          portfolioData={selectedData}
          setOpen={setShowTradesModal}
          allTradesOfPortfolio={allTradesOfPortfolio}
        />
      )}

      <GKModal
        modalTitle="View Rationale"
        open={openRationalModal}
        setOpen={setOpenRationalModal}
        footer={
          <GKButton
            variant="outlined"
            color="dark"
            onClick={() => {
              setOpenRationalModal(false);
            }}
          >
            Close
          </GKButton>
        }
      >
        <Grid2 container>
          <Grid2 size={12}>
            <Typography fontSize={14} fontWeight={500}>
              {htmlToText(state.data.rationale)}
            </Typography>
          </Grid2>
        </Grid2>
      </GKModal>
    </DashboardLayout>
  );
};

export default OrderManagerPortfolios;
